/**
 * Google Maps Component
 *
 * To use this component, you need to set up a Google Maps API key:
 * 1. Go to https://console.cloud.google.com/google/maps-apis/
 * 2. Create a new project or select an existing one
 * 3. Enable the Maps JavaScript API
 * 4. Create an API key
 * 5. Create a .env.local file in the project root and add:
 *    VITE_GOOGLE_MAPS_API_KEY=your-actual-api-key-here
 *
 * The component displays:
 * - Home location: Włodarzewska 57B, Warsaw
 * - Work location: Aleje Jerozolimskie 181B, Warsaw
 * - Current location (if provided via props)
 */

import {
  Map,
  AdvancedMarker,
  InfoWindow,
  Pin,
} from "@vis.gl/react-google-maps";
import { useState } from "react";
import { MAP_LOCATIONS } from "@/lib/constants";
import { useTranslation } from "react-i18next";

interface GoogleMapProps {
  currentLocation?: {
    lat: number;
    lng: number;
  };
  height?: string;
  zoom?: number;
}

export default function GoogleMap({
  currentLocation,
  height = "300px",
  zoom = 13,
}: GoogleMapProps) {
  const { t } = useTranslation();
  const [openInfoWindow, setOpenInfoWindow] = useState<string | null>(null);

  // Use Warsaw as default center, or current location if available
  const center = currentLocation || MAP_LOCATIONS.WARSAW.coordinates;

  type MapMarker = {
    id: string;
    position: { lat: number; lng: number };
    title: string;
    description: string;
    color: string;
  };

  const markers: MapMarker[] = [
    {
      id: "home",
      position: MAP_LOCATIONS.HOME.coordinates,
      title: MAP_LOCATIONS.HOME.name,
      description: MAP_LOCATIONS.HOME.address,
      color: "#22C55E", // green
    },
    {
      id: "work",
      position: MAP_LOCATIONS.WORK.coordinates,
      title: MAP_LOCATIONS.WORK.name,
      description: MAP_LOCATIONS.WORK.address,
      color: "#3B82F6", // blue
    },
  ];

  // Add current location marker if available and different from default locations
  if (currentLocation) {
    markers.push({
      id: "current",
      position: currentLocation,
      title: t("currentLocation"),
      description: "Live location",
      color: "#EF4444", // red
    });
  }

  return (
    <div
      style={{ height, width: "100%" }}
      className="rounded-lg overflow-hidden"
    >
      <Map
        mapId={import.meta.env.VITE_GOOGLE_MAPS_MAP_ID}
        defaultCenter={center}
        defaultZoom={zoom}
        style={{ width: "100%", height: "100%" }}
        gestureHandling="greedy"
        disableDefaultUI={false}
        zoomControl={true}
        mapTypeControl={false}
        streetViewControl={false}
        fullscreenControl={true}
        clickableIcons={true}
      >
        {markers.map((marker) => (
          <AdvancedMarker
            key={marker.id}
            position={marker.position}
            title={marker.title}
            onClick={() => setOpenInfoWindow(marker.id)}
          >
            <Pin
              background={marker.color}
              borderColor={marker.color}
              glyphColor="#FFFFFF"
            />
          </AdvancedMarker>
        ))}

        {openInfoWindow && (
          <InfoWindow
            position={markers.find((m) => m.id === openInfoWindow)?.position}
            onCloseClick={() => setOpenInfoWindow(null)}
          >
            <div className="p-2">
              <h3 className="font-semibold text-sm">
                {markers.find((m) => m.id === openInfoWindow)?.title}
              </h3>
              <p className="text-xs text-gray-600">
                {markers.find((m) => m.id === openInfoWindow)?.description}
              </p>
            </div>
          </InfoWindow>
        )}
      </Map>
    </div>
  );
}
